import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";

import {
  Alert as MuiAlert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  CardHeader,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { spacing } from "@mui/system";
import PropTypes from "prop-types";

import SaveCancel from "../../../components/SaveCancel";
import TabPanel from "../../../components/TabPanel";
import { a11yProps } from "../../../utils/common";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";
import useAuth from "../../../hooks/useAuth";
import useEmployee from "../../../hooks/useEmployee";
import EmployeeFields from "./components/EmployeeFields";
import MenuAclFields from "./components/MenuAclFields";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Card = styled(MuiCard)(spacing);

const Alert = styled(MuiAlert)(spacing);

const useTabStyles = styled({
  root: {
    justifyContent: "center",
    width: "1000px",
  },
  scroller: {
    flexGrow: "0",
  },
});

const validationSchema = Yup.object().shape({
  businessType: Yup.string().required("Required"),
  tenant: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required(),
  branchName: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required(),
  emailId: Yup.string().required("Required"),
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
  userName: Yup.string().required("Required"),
  address: Yup.string().required("Required"),
  mobileNo: Yup.string()
    .required("Required")
    .matches(/^\d{10}$/, "Must be a valid phone number with up to 10 digits"),
  status: Yup.string().required("Required"),
  password: Yup.string().required("Required"),
  confirmPassword: Yup.string()
    .required("Password is required")
    .when("password", {
      is: (val) => (val && val.length > 0 ? true : false),
      then: () => Yup.string().oneOf([Yup.ref("password")], "Both password need to be the same"),
    }),
});

function AddEmployee() {
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const classes = useTabStyles();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const initialValues = {
    tenant: {},
    branchName: {},
    mobileNo: "",
    password: "",
    confirmPassword: "",
    emailId: "",
    firstName: "",
    lastName: "",
    userName: "",
    address: "",
    status: "ACTIVE",
  };

  const methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const {
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = methods;

  const { t } = useTranslation();
  const { addEmployee, isSubmitting } = useEmployee();
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (
      user &&
      (user.businessType != "SA" || user.businessType != "DST" || user.businessType != "MSO")
    ) {
      setValue("tenant", {
        label: user.name,
        value: user.tenantId,
      });
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      setValue("tenant", {
        label: user.name,
        value: user.tenantId,
      });
    }
  }, [user]);

  const navigate = useNavigate();

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>C-Panel</Typography>
        <Typography>Employee</Typography>
        <Typography>Add</Typography>
      </Breadcrumbs>
    );
  };

  const onSubmit = async (data) => {
    try {
      data["tenant"] = {
        tenantId: data.tenant.value,
      };
      if (data.branchName.value != "0") {
        data["branch"] = {
          branchId: data.branchName.value,
        };
      }

      let menuAcls = [];
      if (selectedPermissions.length == 0) {
        dispatch(showErrorSnackbar("Please select the menu acl"));
        return;
      }
      for (const index in selectedPermissions) {
        const key = index.split("-");
        menuAcls.push({
          rolePermissionId: Number(key[0]),
          isEnabled: selectedPermissions[index],
        });
      }
      data["menuAcl"] = menuAcls;
      console.log(menuAcls, "menuAcls");

      console.log(data, "employee data fffff");

      await addEmployee(data);
      dispatch(showSuccessSnackbar("Employee added successfully!"));
      reset();
      cancelBtn();
    } catch (err) {
      dispatch(showErrorSnackbar(err.message));
    }
  };

  const cancelBtn = () => {
    navigate("/cpanel/employee/list");
  };

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <Card mb={6}>
          <CardHeader title={t("add_employee")} subheader={breadCrump()} />
          <CardContent>
            {isSubmitting ? (
              <Box display="flex" justifyContent="center" my={6}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    classes={{ scroller: classes.scroller }}
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons
                    allowScrollButtonsMobile
                    aria-label="tenant"
                    sx={{
                      justifyContent: "center",
                      width: {
                        xs: "100%", // Full width on mobile devices
                        sm: "500px", // Fixed width on small devices
                        md: "700px", // Wider on medium devices
                        lg: "900px", // Even wider on larger devices
                      },
                      minWidth: "300px", // Ensure a minimum width across all devices
                      maxWidth: "100%", // Ensure it doesn't overflow the container
                    }}
                  >
                    {" "}
                    <Tab label="Franchise Details" {...a11yProps(0)} />
                    <Tab label="Menu Acl" {...a11yProps(0)} />
                  </Tabs>
                  <TabPanel value={tabValue} index={0}>
                    <EmployeeFields fields={validationSchema.fields} />
                  </TabPanel>
                  <TabPanel value={tabValue} index={1}>
                    <MenuAclFields
                      fields={validationSchema.fields}
                      selectedPermissions={selectedPermissions}
                      setSelectedPermissions={setSelectedPermissions}
                    />
                  </TabPanel>
                </Box>

                <SaveCancel cancelBtn={cancelBtn} resetForm={reset} />
              </form>
            )}
          </CardContent>
        </Card>
      </FormProvider>
    </React.Fragment>
  );
}

export default AddEmployee;
